import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Send, Trash2, RefreshCw, ChevronDown, Sparkles, Wallet, BarChart3,
  ArrowLeftRight, Lightbulb, Zap, ArrowRight, ArrowUpRight, Paperclip, Mic,
  ShoppingCart, Banknote, PieChart, FileText, CalendarDays, Receipt, MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EXTRATO_IMAGEM_TIPOS_ACEITOS = ['image/jpeg', 'image/png', 'image/webp'];
const EXTRATO_IMAGEM_MAX_BYTES = 8 * 1024 * 1024;

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/** Convert *bold* and _italic_ WhatsApp/Gastify markdown to JSX */
function renderText(text) {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, i) => {
    const parts = line.split(/(\*[^*]+\*)/g).map((part, j) => {
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return <strong key={j}>{part.slice(1, -1)}</strong>;
      }
      return part.split(/(_[^_]+_)/g).map((p, k) => {
        if (p.startsWith('_') && p.endsWith('_') && p.length > 2) {
          return <em key={k}>{p.slice(1, -1)}</em>;
        }
        return p;
      });
    });
    return (
      <React.Fragment key={i}>
        {parts}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
}

// ── Avatar do assistente ─────────────────────────────────────────────────────

function AiAvatar({ size = 32 }) {
  return (
    <div
      className="rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-[0_0_14px_rgba(59,130,246,0.45)]"
      style={{ width: size, height: size }}
    >
      <Sparkles size={Math.round(size * 0.5)} className="text-white" />
    </div>
  );
}

// ── Arte do hero: azulejo de vidro com estrela luminosa (claro e escuro) ─────

function AiOrb() {
  return (
    <div className="relative w-[200px] h-[150px] lg:w-[230px] lg:h-[170px] flex-shrink-0 select-none" aria-hidden="true">
      <style>{`
        @keyframes orbFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-5px) } }
        .orb-float { animation: orbFloat 6s ease-in-out infinite; }
      `}</style>
      <div
        className="absolute left-1/2 bottom-0 w-44 h-12 -translate-x-1/2"
        style={{ background: 'radial-gradient(closest-side, rgba(59,130,246,0.55), rgba(59,130,246,0))' }}
      />
      <svg viewBox="0 0 280 220" className="absolute inset-0 w-full h-full overflow-visible">
        <defs>
          <linearGradient id="orbRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#6366F1" stopOpacity="0.1" />
          </linearGradient>
        </defs>
        <ellipse cx="140" cy="112" rx="132" ry="58" transform="rotate(-14 140 112)" fill="none" stroke="url(#orbRing)" strokeWidth="1.4" />
        <ellipse cx="140" cy="112" rx="112" ry="48" transform="rotate(10 140 112)" fill="none" stroke="url(#orbRing)" strokeWidth="1.1" opacity="0.75" />
        <circle cx="30" cy="104" r="2.4" fill="#3B82F6" />
        <circle cx="244" cy="60" r="1.8" fill="#60A5FA" />
      </svg>
      <div className="orb-float absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[55%]">
        <div
          className="w-[92px] h-[92px] lg:w-[108px] lg:h-[108px] rounded-[28px] flex items-center justify-center -rotate-[10deg]
            border border-white/80 bg-gradient-to-br from-white via-blue-50 to-blue-200
            shadow-[0_20px_40px_-10px_rgba(37,99,235,0.55),inset_0_1px_2px_rgba(255,255,255,0.9)]
            dark:border-white/25 dark:from-blue-600/55 dark:via-slate-900/90 dark:to-slate-950
            dark:shadow-[0_22px_50px_-12px_rgba(37,99,235,0.65),inset_0_1px_1px_rgba(255,255,255,0.35),inset_0_-10px_24px_rgba(37,99,235,0.25)]"
        >
          <svg
            viewBox="0 0 64 64"
            className="w-[56%] h-[56%] text-blue-600 dark:text-white [filter:drop-shadow(0_0_6px_rgba(37,99,235,0.55))] dark:[filter:drop-shadow(0_0_7px_rgba(96,165,250,0.95))_drop-shadow(0_0_16px_rgba(59,130,246,0.7))]"
          >
            <path
              d="M32 6C34 20 44 30 58 32C44 34 34 44 32 58C30 44 20 34 6 32C20 30 30 20 32 6Z"
              fill="none" stroke="currentColor" strokeWidth="3.8" strokeLinejoin="round" strokeLinecap="round"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

// ── TypingIndicator ──────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex items-end gap-2.5 mb-3">
      <AiAvatar />
      <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-white dark:bg-white/5 border border-slate-200/80 dark:border-white/10">
        <div className="flex gap-1 items-center h-4">
          <span className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

// ── MessageBubble ────────────────────────────────────────────────────────────

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex items-end gap-2.5 mb-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {!isUser && <AiAvatar />}

      <div className={`max-w-[80%] sm:max-w-[70%] ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
        <div
          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
            isUser
              ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-br-md shadow-[0_8px_22px_-8px_rgba(59,130,246,0.7)]'
              : 'bg-white dark:bg-white/5 text-slate-800 dark:text-slate-100 rounded-bl-md border border-slate-200/80 dark:border-white/10'
          }`}
        >
          {renderText(msg.content)}
        </div>
        <span className="text-[10px] mt-1 px-1 text-slate-400">{formatTime(msg.ts)}</span>
      </div>
    </div>
  );
}

// ── QuickReplies ─────────────────────────────────────────────────────────────

function QuickReplies({ replies, onSelect, disabled }) {
  if (!replies || replies.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 pb-3">
      {replies.map((r, i) => (
        <button
          key={i}
          disabled={disabled}
          onClick={() => onSelect(r)}
          className="px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all
            bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-300
            border-blue-200 dark:border-blue-400/30
            hover:bg-blue-100 dark:hover:bg-blue-500/20
            disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {r}
        </button>
      ))}
    </div>
  );
}

// ── Estado inicial (hero + ações rápidas + exemplos) ─────────────────────────

const TONES = {
  emerald: 'bg-emerald-100 text-emerald-600 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:ring-emerald-500/20',
  violet: 'bg-violet-100 text-violet-600 ring-1 ring-violet-200 dark:bg-violet-500/15 dark:text-violet-400 dark:ring-violet-500/20',
  blue: 'bg-blue-100 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-500/15 dark:text-blue-400 dark:ring-blue-500/20',
  amber: 'bg-amber-100 text-amber-600 ring-1 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:ring-amber-500/20',
  rose: 'bg-rose-100 text-rose-600 ring-1 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-400 dark:ring-rose-500/20',
};

const ACTIONS = [
  { id: 'gasto', title: 'Registrar gasto', desc: 'Adicione um novo gasto no sistema', icon: Wallet, tone: 'emerald', mode: 'fill', text: 'Gastei ' },
  { id: 'resumo', title: 'Resumo financeiro', desc: 'Mostre meu resumo do mês', icon: BarChart3, tone: 'violet', mode: 'send', text: 'resumo financeiro' },
  { id: 'transferencia', title: 'Nova transferência', desc: 'Registre uma transferência', icon: ArrowLeftRight, tone: 'blue', mode: 'page' },
  { id: 'analise', title: 'Análise de gastos', desc: 'Me ajude a analisar meus gastos', icon: Lightbulb, tone: 'amber', mode: 'send', text: 'me ajude a analisar meus gastos' },
];

const EXAMPLE_POOL = [
  { text: 'Gastei 150 no mercado hoje', icon: ShoppingCart, tone: 'emerald' },
  { text: 'Recebi 2.000 de salário', icon: Banknote, tone: 'amber' },
  { text: 'Qual meu saldo atual?', icon: PieChart, tone: 'violet' },
  { text: 'Me mostra um resumo financeiro', icon: FileText, tone: 'rose' },
  { text: 'Quais foram meus maiores gastos do mês?', icon: CalendarDays, tone: 'blue' },
  { text: 'Crie um gráfico dos meus gastos', icon: BarChart3, tone: 'blue' },
  { text: 'Paguei 89,90 de internet ontem', icon: Receipt, tone: 'rose' },
  { text: 'Quanto gastei com alimentação este mês?', icon: ShoppingCart, tone: 'emerald' },
  { text: 'Comprei um tênis de 300 em 3x no cartão', icon: ShoppingCart, tone: 'violet' },
  { text: 'Recebi 500 de freelance no PIX', icon: Banknote, tone: 'amber' },
  { text: 'Quais contas vencem esta semana?', icon: CalendarDays, tone: 'blue' },
  { text: 'Quanto sobrou do meu salário?', icon: PieChart, tone: 'violet' },
];

function pickExamples(seed) {
  const start = (seed * 6) % EXAMPLE_POOL.length;
  return Array.from({ length: 6 }, (_, i) => EXAMPLE_POOL[(start + i) % EXAMPLE_POOL.length]);
}

function SectionLabel({ icon: Icon, children, right }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <p className="flex items-center gap-2 text-[11px] font-bold tracking-[0.16em] uppercase text-blue-600 dark:text-slate-400">
        <Icon size={14} className="text-blue-500" />
        {children}
      </p>
      {right}
    </div>
  );
}

function EmptyState({ onAction, onExample }) {
  const [seed, setSeed] = useState(0);
  const examples = useMemo(() => pickExamples(seed), [seed]);

  return (
    <div className="space-y-4 sm:space-y-5 py-1">
      {/* Hero */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-[10px] sm:text-[11px] font-bold tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400 mb-2">
            <span className="w-[3px] h-3.5 rounded-full bg-blue-500" />
            Seu assistente financeiro
          </p>
          <h1
            className="text-[26px] sm:text-4xl leading-[1.1] font-extrabold tracking-tight text-slate-900 dark:text-white"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Como posso te{' '}
            <span className="text-blue-600 dark:text-blue-400">ajudar</span> hoje?
          </h1>
          <p className="mt-2 sm:mt-3 text-[13px] sm:text-sm text-slate-600 dark:text-slate-400 max-w-md leading-relaxed">
            Registre movimentações, consulte informações ou execute ações no sistema usando{' '}
            <span className="text-blue-600 dark:text-blue-300 font-medium">linguagem natural</span>.
          </p>
        </div>
        <div className="hidden md:block"><AiOrb /></div>
      </div>

      {/* Ações rápidas */}
      <div>
        <SectionLabel icon={Zap}>Ações rápidas</SectionLabel>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {ACTIONS.map(({ id, title, desc, icon: Icon, tone, mode, text }) => (
            <button
              key={id}
              type="button"
              onClick={() => onAction({ id, mode, text })}
              data-testid={`chat-action-${id}`}
              className="fin-card group w-full text-left p-3 sm:p-4 flex flex-col gap-2.5 cursor-pointer transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <span className="flex items-start justify-between">
                <span className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center ${TONES[tone]}`}>
                  <Icon size={20} />
                </span>
                <span className="w-7 h-7 rounded-full border border-slate-200 dark:border-white/10 flex items-center justify-center text-slate-400 group-hover:text-blue-600 group-hover:border-blue-300 transition-colors">
                  <ArrowRight size={14} />
                </span>
              </span>
              <span className="block min-w-0">
                <span className="block text-[13px] sm:text-sm font-bold text-slate-900 dark:text-slate-100 leading-tight">{title}</span>
                <span className="block text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{desc}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Exemplos (escondidos em celulares muito baixos para não gerar rolagem) */}
      <div className="max-sm:[@media(max-height:720px)]:hidden">
        <SectionLabel
          icon={MessageCircle}
          right={
            <button
              type="button"
              onClick={() => setSeed(s => s + 1)}
              title="Mostrar outros exemplos"
              aria-label="Mostrar outros exemplos"
              className="p-1.5 -my-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-white/5 transition-colors"
            >
              <RefreshCw size={15} />
            </button>
          }
        >
          Experimente perguntar
        </SectionLabel>
        <div className="fin-card p-1.5 grid grid-cols-1 sm:grid-cols-2 sm:gap-x-2">
          {examples.map(({ text, icon: Icon, tone }, idx) => (
            <button
              key={text}
              type="button"
              onClick={() => onExample(text)}
              className={`${idx >= 3 ? 'hidden sm:flex' : 'flex'} group items-center justify-between gap-3 px-2.5 py-2 rounded-xl text-left text-[13px]
                text-slate-800 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors cursor-pointer`}
            >
              <span className="flex items-center gap-2.5 min-w-0">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${TONES[tone]}`}>
                  <Icon size={14} />
                </span>
                <span className="truncate">“{text}”</span>
              </span>
              <ArrowUpRight size={14} className="text-slate-300 group-hover:text-blue-500 dark:text-slate-500 flex-shrink-0 transition-colors" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Chat Component ──────────────────────────────────────────────────────

export default function Chat() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();

  const [messages, setMessages]             = useState([]);
  const [input, setInput]                   = useState('');
  const [loading, setLoading]               = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [quickReplies, setQuickReplies]     = useState([]);
  const [showScrollBtn, setShowScrollBtn]   = useState(false);

  const bottomRef    = useRef(null);
  const inputRef     = useRef(null);
  const messagesRef  = useRef(null);
  const fileInputRef = useRef(null);

  const headers = useCallback(() => {
    const token = localStorage.getItem('fin_token') || '';
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Tenant-Slug': tenantSlug || '',
    };
  }, [tenantSlug]);

  // ── Load history ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!tenantSlug) return;
    setHistoryLoading(true);
    fetch(`${API_URL}/api/chat/history?limit=100`, { headers: headers() })
      .then(r => r.json())
      .then(data => {
        const msgs = data.messages || [];
        setMessages(msgs);
        setQuickReplies([]);
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [tenantSlug, headers]);

  // ── Auto-scroll ───────────────────────────────────────────────────────────
  const scrollToBottom = useCallback((smooth = true) => {
    bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  useEffect(() => {
    if (!historyLoading) {
      scrollToBottom(false);
    }
  }, [historyLoading, scrollToBottom]);

  useEffect(() => {
    if (!loading) {
      scrollToBottom(true);
    }
  }, [messages, loading, scrollToBottom]);

  // ── Scroll button visibility ──────────────────────────────────────────────
  const handleScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distFromBottom > 120);
  };

  // ── Send message ──────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setQuickReplies([]);

    const userMsg = { role: 'user', content: trimmed, ts: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const resp = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ message: trimmed }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao enviar mensagem');
      }

      const data = await resp.json();
      const botMsg = {
        role: 'bot',
        content: data.response,
        ts: new Date().toISOString(),
      };
      setMessages(prev => [...prev, botMsg]);
      setQuickReplies(data.quickReplies || []);
    } catch (err) {
      toast.error(err.message || 'Erro ao conectar com o assistente');
      setMessages(prev => [...prev, {
        role: 'bot',
        content: '⚠️ Erro ao processar sua mensagem. Tente novamente.',
        ts: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, loading, headers]);

  // ── Send print de extrato (imagem) ──────────────────────────────────────────
  const sendImage = useCallback(async (file) => {
    if (!file || loading) return;
    if (!EXTRATO_IMAGEM_TIPOS_ACEITOS.includes(file.type)) {
      toast.error('Envie uma imagem JPEG, PNG ou WEBP.');
      return;
    }
    if (file.size > EXTRATO_IMAGEM_MAX_BYTES) {
      toast.error('A imagem excede o limite de 8 MB.');
      return;
    }

    setQuickReplies([]);
    const userMsg = { role: 'user', content: '📷 Print de extrato enviado', ts: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const token = localStorage.getItem('fin_token') || '';
      const form = new FormData();
      form.append('file', file);
      // Sem 'Content-Type' aqui — o browser define o boundary do multipart automaticamente.
      const resp = await fetch(`${API_URL}/api/chat/message-imagem`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Slug': tenantSlug || '' },
        body: form,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao enviar a imagem');
      }

      const data = await resp.json();
      setMessages(prev => [...prev, { role: 'bot', content: data.response, ts: new Date().toISOString() }]);
      setQuickReplies(data.quickReplies || []);
    } catch (err) {
      toast.error(err.message || 'Erro ao conectar com o assistente');
      setMessages(prev => [...prev, {
        role: 'bot',
        content: '⚠️ Erro ao processar a imagem. Tente novamente.',
        ts: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [loading, tenantSlug]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite escolher o mesmo arquivo de novo depois
    if (file) sendImage(file);
  };

  // ── Clear history ─────────────────────────────────────────────────────────
  const clearHistory = async () => {
    if (!window.confirm('Limpar todo o histórico do chat?')) return;
    try {
      await fetch(`${API_URL}/api/chat/history`, { method: 'DELETE', headers: headers() });
      setMessages([]);
      setQuickReplies([]);
      toast.success('Histórico limpo!');
    } catch {
      toast.error('Erro ao limpar histórico');
    }
  };

  // ── Key handler ───────────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const fillInput = (text) => {
    setInput(text);
    setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(text.length, text.length);
    }, 0);
  };

  const handleAction = ({ mode, text }) => {
    if (mode === 'send') sendMessage(text);
    else if (mode === 'fill') fillInput(text);
    else navigate(`/${tenantSlug}/lancamentos?acao=transferencia`);
  };

  const isEmpty = !historyLoading && messages.length === 0;

  return (
    <div className="flex flex-col h-[calc(100dvh-7.5rem)] lg:h-[calc(100vh-2rem)] max-w-5xl mx-auto w-full">
      {/* ── Header (só com conversa em andamento) ───────────────────────────── */}
      {!isEmpty && !historyLoading && (
        <div className="flex items-center justify-between pb-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <AiAvatar size={40} />
            <div>
              <p className="font-bold text-slate-800 dark:text-slate-100 text-sm leading-tight">Assistente IA</p>
              <p className="text-xs text-emerald-500 font-medium flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                Online
              </p>
            </div>
          </div>
          <button
            onClick={clearHistory}
            title="Limpar histórico"
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 text-slate-400 hover:text-red-500 transition-colors"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}

      {/* ── Messages / Estado inicial ───────────────────────────────────────── */}
      <div
        ref={messagesRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto relative min-h-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {historyLoading ? (
          <div className="flex items-center justify-center h-full">
            <RefreshCw size={20} className="animate-spin text-blue-400" />
          </div>
        ) : isEmpty ? (
          <EmptyState onAction={handleAction} onExample={fillInput} />
        ) : (
          <div className="pt-1">
            {messages.map((msg, i) => (
              <MessageBubble key={i} msg={msg} />
            ))}
            {loading && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>
        )}

        {showScrollBtn && (
          <button
            onClick={() => scrollToBottom(true)}
            className="sticky bottom-3 float-right mr-2 w-9 h-9 rounded-full bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-white/10 flex items-center justify-center text-slate-500 hover:text-blue-500 transition-all hover:scale-110"
          >
            <ChevronDown size={18} />
          </button>
        )}
      </div>

      {/* ── Quick replies ───────────────────────────────────────────────────── */}
      {quickReplies.length > 0 && (
        <div className="flex-shrink-0 pt-2">
          <QuickReplies replies={quickReplies} onSelect={sendMessage} disabled={loading} />
        </div>
      )}

      {/* ── Input ───────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 pt-3">
        <div className="fin-card !rounded-[26px] p-2 flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={EXTRATO_IMAGEM_TIPOS_ACEITOS.join(',')}
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            type="button"
            disabled={loading}
            onClick={() => fileInputRef.current?.click()}
            title="Enviar print de extrato"
            aria-label="Enviar print de extrato"
            data-testid="chat-attach"
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 bg-slate-100 dark:bg-white/5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Paperclip size={18} />
          </button>

          <textarea
            ref={inputRef}
            value={input}
            onChange={e => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder="Digite um comando ou pergunta..."
            rows={1}
            disabled={loading}
            data-testid="chat-input"
            className="flex-1 resize-none bg-transparent px-2 py-3 text-sm text-slate-800 dark:text-slate-100
              placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none
              disabled:opacity-60 min-h-[44px] max-h-[120px] overflow-y-auto leading-relaxed"
            style={{ scrollbarWidth: 'thin' }}
          />

          <button
            type="button"
            disabled
            title="Comando de voz — em breve"
            aria-label="Comando de voz (em breve)"
            className="w-11 h-11 rounded-2xl hidden sm:flex items-center justify-center flex-shrink-0 text-slate-400 cursor-not-allowed"
          >
            <Mic size={18} />
          </button>

          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            aria-label="Enviar"
            data-testid="chat-send"
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0
              bg-gradient-to-br from-blue-500 to-blue-700 text-white
              shadow-[0_8px_20px_-6px_rgba(37,99,235,0.85)]
              hover:from-blue-400 hover:to-blue-600 active:scale-95
              disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all"
          >
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <Send size={17} />}
          </button>
        </div>
        <p className="text-center text-[10px] text-slate-400 dark:text-slate-500 mt-2">
          Enter para enviar · Shift + Enter para nova linha
        </p>
      </div>
    </div>
  );
}
